/** Compact number formatter — 1.2K, 3.4M, etc. */
export function fmtNum(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return Math.round(n).toString();
}

/**
 * Format a decimal stat (percentage, multiplier, ratio) to exactly 2 decimal
 * places, for every "small decimal" figure in the Analysis tab.
 *
 * - `null`/`undefined`/exactly `0` → `null`, so callers hide the stat
 *   entirely instead of rendering a bare "0" that reads as "no data".
 * - A genuinely nonzero value that rounds to "0.00" (e.g. 0.0004) is
 *   floored to "0.01" (sign-preserved) instead — a real, tiny signal
 *   shouldn't display identically to true zero.
 *
 * Returns a plain numeric string with no unit — callers append their own
 * suffix ("%", "×", etc.).
 */
export function fmtDecimal2(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n) || n === 0) return null;
  const rounded = Math.round(Math.abs(n) * 100) / 100;
  const floor = rounded === 0 ? 0.01 : rounded;
  return (n < 0 ? -floor : floor).toFixed(2);
}

/** Format a raw channel metric string (e.g. "1,234,567" → "1.2M"). */
export function fmtChannelMetric(v: unknown): string {
  const s = String(v ?? '').trim();
  if (!s) return '—';
  if (/[a-zA-Z]/.test(s)) return s;
  const n = Number(s.replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return s;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-IN');
}

/**
 * Map a Phyllo price-explanation level string to one of the three Badge
 * tones (good / fair / poor).
 *
 * Phyllo's vocabulary is open-ended (e.g., "Average", "Mid-Tier
 * Influencer", "Tier 3", "Long-tail", "Long-form Video", "Standard"),
 * so unknown levels fall through to 'fair' (neutral gray) — better than
 * painting an unknown red. Only confidently-bad terms map to 'poor'.
 */
export function levelToBadgeType(level: string): 'good' | 'fair' | 'poor' {
  const l = level.toUpperCase();
  if (l.includes('HIGH') || l.includes('EXCELLENT') || l.includes('STRONG') || l.includes('MID-TIER')) {
    return 'good';
  }
  if (l.includes('LOW') || l.includes('WEAK') || l.includes('TIER 3') || l === 'LONG-TAIL') {
    return 'poor';
  }
  return 'fair';
}

/**
 * Convert an ISO timestamp string to a short relative-time string.
 * Returns 'just now' for <1 min, 'Nm ago' for <1 hour, 'Nh ago' for
 * <1 day, 'Nd ago' for <30 days, and a 'Mmm DD' absolute date for older.
 *
 * Returns an empty string if the input is null/undefined or unparsable.
 * No external dependency — uses native Date arithmetic.
 */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const now = Date.now();
  const diffMs = now - then;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(then).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
